import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { useMutation } from "@apollo/client";
import { useForm, Controller } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { FacilityCourseType, FacilityOperationType } from "@/types";
import { PATCH_FACILITY_MUTATION, FacilityDetailsContactQueryResponse } from "@/features/facility/queries";
import { EditProps } from "./types";
import {
  NullableStringSchema,
  NullableNumberSchema,
  YesNoNullOptionsSchema,
  yesNoNullOptionToNullableBoolean,
  nullableBooleanToYesNoNullOption
} from "./validation";

const CourseDetailsSchema = z.object({
  courseType: NullableStringSchema,
  operationType: NullableStringSchema,
  lengthOfSeasonInMonths: NullableNumberSchema,
  holesInRegulation: NullableNumberSchema,
  practiceRangeAvailable: YesNoNullOptionsSchema,
  simulatorAvailable: YesNoNullOptionsSchema,
  golfCartBrand: NullableStringSchema,
  membershipTotal: NullableNumberSchema,
  membershipMen: NullableNumberSchema,
  membershipWomen: NullableNumberSchema,
  membershipJunior: NullableNumberSchema,
  membershipSenior: NullableNumberSchema
});

type FieldValues = z.infer<typeof CourseDetailsSchema>;

interface CourseDetailsEditFormProps extends EditProps {
  courseType?: FacilityCourseType | null;
  operationType?: FacilityOperationType | null;
  lengthOfSeasonInMonths?: number | null;
  holesInRegulation?: number | null;
  practiceRangeAvailable?: boolean | null;
  simulatorAvailable?: boolean | null;
  golfCartBrand?: string | null;
  membershipTotal?: number | null;
  membershipMen?: number | null;
  membershipWomen?: number | null;
  membershipJunior?: number | null;
  membershipSenior?: number | null;
  courseTypeOptions: FacilityDetailsContactQueryResponse["courseTypeOptions"];
  operationTypeOptions: FacilityDetailsContactQueryResponse["operationTypeOptions"];
}

const defaultErrorMessage = "Failed to update course details";

export default function CourseDetailsEditForm({
  facilityId,
  courseType: initialCourseType,
  operationType: initialOperationType,
  lengthOfSeasonInMonths: initialLengthOfSeasonInMonths,
  holesInRegulation: initialHolesInRegulation,
  practiceRangeAvailable: initialPracticeRangeAvailable,
  simulatorAvailable: initialSimulatorAvailable,
  golfCartBrand: initialGolfCartBrand,
  membershipTotal: initialMembershipTotal,
  membershipMen: initialMembershipMen,
  membershipWomen: initialMembershipWomen,
  membershipJunior: initialMembershipJunior,
  membershipSenior: initialMembershipSenior,
  courseTypeOptions,
  operationTypeOptions,
  onCancel,
  onSuccess,
  onError
}: CourseDetailsEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      courseType: initialCourseType || "",
      operationType: initialOperationType || "",
      lengthOfSeasonInMonths: initialLengthOfSeasonInMonths,
      holesInRegulation: initialHolesInRegulation,
      practiceRangeAvailable: initialPracticeRangeAvailable,
      simulatorAvailable: initialSimulatorAvailable,
      golfCartBrand: initialGolfCartBrand || "",
      membershipTotal: initialMembershipTotal,
      membershipMen: initialMembershipMen,
      membershipWomen: initialMembershipWomen,
      membershipJunior: initialMembershipJunior,
      membershipSenior: initialMembershipSenior
    },
    resolver: zodResolver(CourseDetailsSchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="course-details-edit-form">
      <Controller
        name="courseType"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            value={value || ""}
            label="Course Type"
            onSelect={onChange}
            options={[
              ...courseTypeOptions.enumValues.map((o) => ({
                value: o.name,
                label: o.description
              })),
              { value: "", displayText: "--" }
            ]}
            disabled={loading}
            error={!!form.formState.errors.courseType}
            errorText={form.formState.errors.courseType?.message}
          />
        )}
      />
      <Controller
        name="operationType"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            value={value || ""}
            label="Operation Type"
            onSelect={onChange}
            options={[
              ...operationTypeOptions.enumValues.map((o) => ({
                value: o.name,
                label: o.description
              })),
              { value: "", displayText: "--" }
            ]}
            disabled={loading}
            error={!!form.formState.errors.operationType}
            errorText={form.formState.errors.operationType?.message}
          />
        )}
      />
      <TextField
        className="w-full"
        label="Length of Season (months)"
        type="number"
        value={form.watch("lengthOfSeasonInMonths")?.toString() || ""}
        {...form.register("lengthOfSeasonInMonths", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.lengthOfSeasonInMonths}
        errorText={form.formState.errors.lengthOfSeasonInMonths?.message}
      />
      <TextField
        className="w-full"
        label="Total Regulation Holes"
        type="number"
        value={form.watch("holesInRegulation")?.toString() || ""}
        {...form.register("holesInRegulation", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.holesInRegulation}
        errorText={form.formState.errors.holesInRegulation?.message}
      />
      <Controller
        name="practiceRangeAvailable"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            label="Practice Range Available"
            value={nullableBooleanToYesNoNullOption(value)}
            onSelect={(v) => onChange(yesNoNullOptionToNullableBoolean(v))}
            options={[{ value: "Yes" }, { value: "No" }, { value: "", label: "--" }]}
            disabled={loading}
            error={!!form.formState.errors.practiceRangeAvailable}
            errorText={form.formState.errors.practiceRangeAvailable?.message}
          />
        )}
      />
      <Controller
        name="simulatorAvailable"
        control={form.control}
        render={({ field: { onChange, value } }) => (
          <Select
            label="Simulator Available"
            value={nullableBooleanToYesNoNullOption(value)}
            onSelect={(v) => onChange(yesNoNullOptionToNullableBoolean(v))}
            options={[{ value: "Yes" }, { value: "No" }, { value: "", label: "--" }]}
            disabled={loading}
            error={!!form.formState.errors.simulatorAvailable}
            errorText={form.formState.errors.simulatorAvailable?.message}
          />
        )}
      />
      <TextField
        className="w-full"
        label="Golf Cart Brand"
        value={form.watch("golfCartBrand") || ""}
        {...form.register("golfCartBrand")}
        disabled={loading}
        error={!!form.formState.errors.golfCartBrand}
        errorText={form.formState.errors.golfCartBrand?.message}
      />
      <TextField
        className="w-full"
        type="number"
        label="Total Membership"
        value={form.watch("membershipTotal")?.toString() || ""}
        {...form.register("membershipTotal", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.membershipTotal}
        errorText={form.formState.errors.membershipTotal?.message}
      />
      <TextField
        className="w-full"
        type="number"
        label="Membership – Men"
        value={form.watch("membershipMen")?.toString() || ""}
        {...form.register("membershipMen", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.membershipMen}
        errorText={form.formState.errors.membershipMen?.message}
      />
      <TextField
        className="w-full"
        type="number"
        label="Membership – Women"
        value={form.watch("membershipWomen")?.toString() || ""}
        {...form.register("membershipWomen", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.membershipWomen}
        errorText={form.formState.errors.membershipWomen?.message}
      />
      <TextField
        className="w-full"
        type="number"
        label="Membership – Juniors"
        value={form.watch("membershipJunior")?.toString() || ""}
        {...form.register("membershipJunior", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.membershipJunior}
        errorText={form.formState.errors.membershipJunior?.message}
      />
      <TextField
        className="w-full"
        type="number"
        label="Membership – Seniors"
        value={form.watch("membershipSenior")?.toString() || ""}
        {...form.register("membershipSenior", { valueAsNumber: true })}
        disabled={loading}
        error={!!form.formState.errors.membershipSenior}
        errorText={form.formState.errors.membershipSenior?.message}
      />
      <div className="flex flex-wrap justify-end gap-1 items-center">
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
}
